import type {
  IHttpRequestParams,
  IProxiedHttpResponse,
} from '../../data-layer-client/FrameMessenger';

import type {
  RequestFilter,
  ResponseFilter,
  Matcher,
  RequestFilterData,
} from './types';
import {authStore} from '../authentication/AuthenticationStore';

import {authenticatedUserFilter} from './authenticatedUserFilter';
import {externalAccountKeyFilter} from './externalAccountKeyFilter';
import {embeddedAuthLinkCreateFilter} from './embeddedAuthLinkCreateFilter';
import {addExternalAccountFilter} from './addExternalAccountFilter';
import type {AuthenticationKeyType} from '../authentication/types';
import {addPayoutMethodFilter} from './addPayoutMethodFilter';

const responseFilters: Array<ResponseFilter> = [
  authenticatedUserFilter,
  externalAccountKeyFilter,
];

const requestFilters: Array<RequestFilter> = [
  embeddedAuthLinkCreateFilter,
  addExternalAccountFilter,
  addPayoutMethodFilter,
];

export const requestMatches = (
  matcher: Matcher,
  request: IHttpRequestParams,
): boolean => {
  const url = new URL(request.url);
  const path = url.pathname;
  const {method, body} = request;

  if (matcher.body) {
    if (body === undefined) return false;
    const convertedBody = typeof body === 'string' ? body : body.toString();
    if (!convertedBody.match(matcher.body)) return false;
  }

  if (matcher.method !== method) {
    return false;
  } else if (typeof matcher.urlPath === 'string') {
    return path === matcher.urlPath;
  } else {
    return !!path.match(matcher.urlPath);
  }
};

export const applyResponseFilters = (
  request: IHttpRequestParams,
  response: IProxiedHttpResponse,
  authStateChangedCallback: () => void,
): IProxiedHttpResponse => {
  if (!response.ok) {
    return response;
  }
  return responseFilters.reduce((response, filter) => {
    const filterMatches = filter.matchers.some((matcher) =>
      requestMatches(matcher, request),
    );
    if (filterMatches) {
      return filter.filterResponse(response, authStateChangedCallback);
    } else {
      return response;
    }
  }, response);
};

export const applyRequestFilters = (request: IHttpRequestParams) => {
  const filteredRequest = requestFilters.reduce(
    (filterData, filter) => {
      const filterMatches = filter.matchers.some((matcher) =>
        requestMatches(matcher, filterData.request),
      );

      if (filterMatches) {
        return filter.filterRequest(filterData);
      } else {
        return filterData;
      }
    },
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {
      request,
      keyType: undefined,
    } as RequestFilterData,
  );

  const {request: processedRequest, keyType: matchedKeyType} = filteredRequest;

  if (!processedRequest.headers) {
    processedRequest.headers = {};
  }

  if (
    !processedRequest.headers.Authorization &&
    !processedRequest.headers.authorization
  ) {
    const requestedKeyType: AuthenticationKeyType | undefined = matchedKeyType;
    const selectedApiKeyAndType = authStore.getApiKey(requestedKeyType);

    // If we were expecting a specific key type, but didn't get it, raise an error.
    if (matchedKeyType && !selectedApiKeyAndType) {
      throw new Error(`User has not stored a key of type: ${matchedKeyType}`);
    }

    if (selectedApiKeyAndType != null) {
      processedRequest.headers.Authorization = `Bearer ${selectedApiKeyAndType?.key}`;
    }

    return {processedRequest, selectedApiKeyType: selectedApiKeyAndType?.type};
  }

  return {processedRequest};
};
